import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import { TextArea } from "@/components/TextArea";
import { LocationPicker } from "@/components/LocationPicker";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  useGetOcurrenceByProtocol,
  useGetCategories,
  useGetSubCategoriesByCategoryId,
  useGetSectors,
  useUpdateOccurrence,
} from "@/hooks/useOccurrences";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  editOccurrenceSchema,
  type EditOccurrenceFormData,
} from "@/schemas/occurrenceSchemas";
import { Loading } from "@/components/Loading";

const priorityOptions = [
  { value: "LOW", label: "Baixa" },
  { value: "MEDIUM", label: "Média" },
  { value: "HIGH", label: "Alta" },
];

export const OccurrenceEdit = () => {
  const navigate = useNavigate();
  const { protocol } = useParams<{ protocol: string }>();
  const [editableAddress, setEditableAddress] = useState("");
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [newAddress, setNewAddress] = useState("");
  const [newFullAddress, setNewFullAddress] = useState<any>(null);
  const [locationJustification, setLocationJustification] = useState("");

  const { data: occurrence, isLoading: isLoadingOccurrence } =
    useGetOcurrenceByProtocol(protocol!);
  const { data: categoriesData } = useGetCategories();
  const { data: sectorsData } = useGetSectors();

  const {
    handleSubmit,
    register,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<EditOccurrenceFormData>({
    resolver: zodResolver(editOccurrenceSchema),
    defaultValues: {
      categoryId: "",
      subcategoryId: "",
      description: "",
      sectorId: "",
      priority: "MEDIUM",
    },
  });

  const categoryId = watch("categoryId");
  const { data: subCategoriesData } = useGetSubCategoriesByCategoryId(
    categoryId || "",
  );

  const updateOccurrence = useUpdateOccurrence();

  useEffect(() => {
    if (occurrence) {
      reset({
        categoryId: occurrence.category.id,
        subcategoryId: occurrence.subcategory.id,
        description: occurrence.description,
        sectorId: occurrence.sector?.id || "",
        priority: occurrence.priority,
      });
      setEditableAddress(occurrence.address || "");
      if (occurrence.latitude && occurrence.longitude) {
        // Garantir que são números válidos
        const lat = parseFloat(String(occurrence.latitude));
        const lng = parseFloat(String(occurrence.longitude));
        if (!isNaN(lat) && !isNaN(lng)) {
          setCoordinates([lat, lng]);
        }
      }
    }
  }, [occurrence, reset]);

  const onSubmit = async (data: EditOccurrenceFormData) => {
    // Validar justificativa se endereço foi alterado
    const addressChanged = (newAddress && newAddress !== occurrence?.address) || 
                          (editableAddress !== occurrence?.address);
    if (addressChanged && !locationJustification) {
      toast.error("Justificativa obrigatória ao alterar a localização");
      return;
    }

    const updateData: any = {
      categoryId: data.categoryId,
      subcategoryId: data.subcategoryId,
      description: data.description,
      sectorId: data.sectorId,
      priority: data.priority,
      address: newAddress || editableAddress,
    };

    // Adicionar justificativa e coordenadas se endereço foi alterado
    if (addressChanged) {
      if (locationJustification) {
        updateData.justification = locationJustification;
      }
      // Adicionar coordenadas se foram alteradas
      if (coordinates) {
        updateData.latitude = coordinates[0];
        updateData.longitude = coordinates[1];
      }
    }

    await updateOccurrence.mutateAsync(
      {
        protocol: occurrence.protocol,
        data: updateData,
      },
      {
        onSuccess: () => {
          toast.success("Ocorrência atualizada com sucesso!");
          navigate(`/occurrences/${protocol}`);
        },
        onError: () => {
          toast.error("Erro ao atualizar ocorrência");
        },
      },
    );
  };

  if (isLoadingOccurrence) {
    return <Loading label="Carregando ocorrência.." />;
  }

  if (!occurrence) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-lg font-medium text-gray-700">
          Ocorrência não encontrada
        </p>
        <Button onClick={() => navigate("/occurrences")} variant="outline">
          Voltar para Ocorrências
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/occurrences/${protocol}`)}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <HeaderPage
            title={`Editar Ocorrência ${occurrence.protocol}`}
            description="Atualize as informações da ocorrência."
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <CardContent
                title="Informações da Ocorrência"
                content={
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Controller
                        name="categoryId"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger
                                title="Categoria"
                                isRequired
                                className={
                                  errors.categoryId ? "border-red-500" : ""
                                }
                              >
                                <SelectValue placeholder="Selecione a categoria" />
                              </SelectTrigger>
                              <SelectContent>
                                {categoriesData?.data?.map((category: any) => (
                                  <SelectItem
                                    key={category.id}
                                    value={category.id}
                                  >
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.categoryId && (
                              <span className="text-sm text-red-500 mt-1 block">
                                {errors.categoryId.message}
                              </span>
                            )}
                          </div>
                        )}
                      />

                      <Controller
                        name="subcategoryId"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={!categoryId}
                            >
                              <SelectTrigger
                                title="Subcategoria"
                                isRequired
                                className={
                                  errors.subcategoryId ? "border-red-500" : ""
                                }
                              >
                                <SelectValue placeholder="Selecione a subcategoria" />
                              </SelectTrigger>
                              <SelectContent>
                                {subCategoriesData?.data?.map(
                                  (subcategory: any) => (
                                    <SelectItem
                                      key={subcategory.id}
                                      value={subcategory.id}
                                    >
                                      {subcategory.name}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                            {errors.subcategoryId && (
                              <span className="text-sm text-red-500 mt-1 block">
                                {errors.subcategoryId.message}
                              </span>
                            )}
                          </div>
                        )}
                      />
                    </div>

                    <TextArea
                      {...register("description")}
                      title="Descrição"
                      placeholder="Descreva a ocorrência detalhadamente..."
                      isRequired
                      errorMessage={errors.description?.message}
                      rows={6}
                    />

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Endereço *
                      </Label>
                      <Input
                        value={editableAddress}
                        onChange={(e) => setEditableAddress(e.target.value)}
                        placeholder="Endereço da ocorrência..."
                        className="text-sm"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Justificativa da alteração de localização *
                      </Label>
                      <Select
                        value={locationJustification}
                        onValueChange={setLocationJustification}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Selecione a justificativa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Localização imprecisa do GPS">
                            Localização imprecisa do GPS
                          </SelectItem>
                          <SelectItem value="Endereço incorreto informado pelo cidadão">
                            Endereço incorreto informado pelo cidadão
                          </SelectItem>
                          <SelectItem value="Ponto ajustado conforme fotos anexadas">
                            Ponto ajustado conforme fotos anexadas
                          </SelectItem>
                          <SelectItem value="Correção com base em conhecimento local">
                            Correção com base em conhecimento local
                          </SelectItem>
                          <SelectItem value="Outro">
                            Outro
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-yellow-600">
                        ⚠️ Campo obrigatório ao alterar endereço ou localização
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Localização no Mapa
                      </Label>
                      {coordinates && (
                        <LocationPicker
                          initialCoordinates={coordinates}
                          onLocationChange={(location) => {
                            setCoordinates(location.coordinates);
                            setNewAddress(location.address);
                            setNewFullAddress(location.fullAddress);
                          }}
                          height="300px"
                        />
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Controller
                        name="sectorId"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger
                                title="Setor Responsável"
                                isRequired
                                className={
                                  errors.sectorId ? "border-red-500" : ""
                                }
                              >
                                <SelectValue placeholder="Selecione o setor" />
                              </SelectTrigger>
                              <SelectContent>
                                {sectorsData?.data?.map((sector: any) => (
                                  <SelectItem key={sector.id} value={sector.id}>
                                    {sector.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.sectorId && (
                              <span className="text-sm text-red-500 mt-1 block">
                                {errors.sectorId.message}
                              </span>
                            )}
                          </div>
                        )}
                      />

                      <Controller
                        name="priority"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger
                                title="Prioridade"
                                isRequired
                                className={
                                  errors.priority ? "border-red-500" : ""
                                }
                              >
                                <SelectValue placeholder="Selecione a prioridade" />
                              </SelectTrigger>
                              <SelectContent>
                                {priorityOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.priority && (
                              <span className="text-sm text-red-500 mt-1 block">
                                {errors.priority.message}
                              </span>
                            )}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                }
              />
            </motion.div>
          </div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-white">
                <CardContentUI className="p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    Informações do Cidadão
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-500">Nome</p>
                      <p className="font-medium">
                        {occurrence.user.profile.fullName}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">E-mail</p>
                      <p className="font-medium break-all">
                        {occurrence.user.email}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Telefone</p>
                      <p className="font-medium">
                        {occurrence.user.profile.phone.replace(
                          /^(\d{2})(\d{5})(\d{4})$/,
                          "($1) $2-$3",
                        )}
                      </p>
                    </div>
                  </div>
                </CardContentUI>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-white">
                <CardContentUI className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Localização</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">{occurrence.address}</p>
                  </div>
                </CardContentUI>
              </Card>
            </motion.div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/occurrences/${protocol}`)}
            className="cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={updateOccurrence.isPending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer"
          >
            <Save className="h-4 w-4 mr-2" />
            Salvar Alterações
          </Button>
        </div>
      </form>
    </>
  );
};
